import React, { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Loader2, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import Crown from "./Crown";

const sessionKey = (appId) => `regalis_chat_session_${appId}`;

const ChatPanel = ({ appId, appName }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(sessionKey(appId)) || "");
  const scrollerRef = useRef(null);

  useEffect(() => {
    const sid = localStorage.getItem(sessionKey(appId));
    if (sid) {
      setSessionId(sid);
      api
        .get(`/apps/${appId}/chat/history`, { params: { session_id: sid } })
        .then((r) => setMessages(r.data))
        .catch(() => {});
    } else {
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only reload when appId changes
  }, [appId]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");

    const userMsg = { id: `local-${Date.now()}`, role: "user", content: text };
    const aiMsgId = `local-ai-${Date.now()}`;
    setMessages((m) => [...m, userMsg, { id: aiMsgId, role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("regalis_token");
      const res = await fetch(`${API}/apps/${appId}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: text, session_id: sessionId || null }),
      });

      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Stream failed");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assembled = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.session_id && !sessionId) {
              setSessionId(obj.session_id);
              localStorage.setItem(sessionKey(appId), obj.session_id);
            }
            if (obj.delta) {
              assembled += obj.delta;
              const snapshot = assembled;
              setMessages((m) =>
                m.map((msg) => (msg.id === aiMsgId ? { ...msg, content: snapshot } : msg)),
              );
            }
            if (obj.error) {
              throw new Error(obj.error);
            }
          } catch (err) {
            // partial SSE line — wait for the next chunk to complete it
            if (process.env.NODE_ENV !== "production") {
              // eslint-disable-next-line no-console
              console.debug("SSE partial line skipped", err);
            }
          }
        }
      }
    } catch (err) {
      toast.error(err.message || "Chat failed");
      setMessages((m) =>
        m.map((msg) =>
          msg.id === aiMsgId
            ? { ...msg, content: msg.content || "_The concierge is unavailable. Try again shortly._" }
            : msg,
        ),
      );
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="regal-card flex flex-col h-[640px] overflow-hidden" data-testid="chat-panel">
      <div className="px-6 py-4 border-b border-seam flex items-center justify-between bg-elevated/40">
        <div>
          <p className="label-eyebrow">Concierge</p>
          <h3 className="font-heading text-2xl mt-1">Ask about {appName}</h3>
        </div>
        <Sparkles className="w-4 h-4 text-champagne" strokeWidth={1.5} />
      </div>

      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto px-6 py-6 space-y-5"
        data-testid="chat-messages"
      >
        {messages.length === 0 && (
          <div className="text-center text-textmuted py-12">
            <Crown className="w-8 h-8 mx-auto mb-3" strokeWidth={1.4} />
            <p className="font-heading text-2xl text-textmain mb-2">
              At your service.
            </p>
            <p className="text-sm max-w-sm mx-auto">
              Teach me how {appName} works in the Knowledge Base, then ask anything —
              from &ldquo;what to do if a dispatch fails&rdquo; to &ldquo;draft a status update&rdquo;.
            </p>
          </div>
        )}

        {messages.map((m) => (
          <div
            key={m.id}
            data-testid={`chat-msg-${m.role}`}
            className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            {m.role === "assistant" && (
              <div className="w-8 h-8 rounded-full bg-elevated border border-champagne/30 flex items-center justify-center shrink-0">
                <Crown className="w-4 h-4" strokeWidth={1.5} />
              </div>
            )}
            <div
              className={`max-w-[78%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-elevated border border-seam rounded-2xl rounded-tr-sm text-textmain"
                  : "bg-elevated/60 border border-white/5 rounded-2xl rounded-tl-sm text-textmain"
              }`}
            >
              {m.content || (streaming ? <span className="text-textmuted">…</span> : "")}
            </div>
            {m.role === "user" && (
              <div className="w-8 h-8 rounded-full bg-elevated border border-seam flex items-center justify-center shrink-0">
                <UserIcon className="w-4 h-4 text-textmuted" strokeWidth={1.5} />
              </div>
            )}
          </div>
        ))}
      </div>

      <form
        onSubmit={send}
        className="border-t border-seam p-4 flex gap-2 bg-obsidian/60"
        data-testid="chat-form"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask the concierge…"
          disabled={streaming}
          data-testid="chat-input"
          className="flex-1 bg-obsidian border border-seam rounded-md px-4 py-2.5 text-sm text-textmain placeholder-[#52525B] focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40 transition-colors"
        />
        <button
          type="submit"
          disabled={streaming || !input.trim()}
          data-testid="chat-send-btn"
          className="px-4 py-2.5 rounded-md bg-champagne text-obsidian font-medium hover:bg-champagne-soft transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          {streaming ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" strokeWidth={1.7} />
          )}
        </button>
      </form>
    </div>
  );
};

export default ChatPanel;
